// Money arithmetic. `packages/contracts` owns the shape and the guard; this owns the sums.
//
// Spec: spec/06-billing-pricing-and-payments.md §1.1–§1.4.
//
// Every amount is an integer in the currency's minor unit. Nothing here ever produces a float, and
// nothing here mixes currencies: one order has one currency (§1.2), so two amounts that disagree
// are a bug at the call site, not something to convert quietly.
import { MONEY_MAX_MINOR_UNITS, MONEY_MIN_MINOR_UNITS, } from '@clavian/contracts';
import { assertInteger, DomainError } from "../errors.js";
import { roundHalfUpBigInt } from "./round.js";
/** 100% in basis points. `250` is 2.50% (spec/06 §1.3). */
export const BASIS_POINTS_SCALE = 10_000;
/** Seconds in an hour: rate cards are quoted per hour and billed per second (spec/06 §4). */
export const SECONDS_PER_HOUR = 3600;
/** An amount of money, checked. Use it instead of an object literal so the range is enforced once. */
export function money(amount, currency) {
    assertInteger(amount, 'amount');
    assertAmountInRange(amount);
    return { amount, currency };
}
/** Zero in a currency. The identity for `addMoney`, and what an empty sum comes to. */
export function zeroMoney(currency) {
    return { amount: 0, currency };
}
export function isZeroMoney(value) {
    return value.amount === 0;
}
export function isNegativeMoney(value) {
    return value.amount < 0;
}
export function addMoney(a, b) {
    assertSameCurrency(a, b);
    return money(a.amount + b.amount, a.currency);
}
export function subtractMoney(a, b) {
    assertSameCurrency(a, b);
    return money(a.amount - b.amount, a.currency);
}
export function negateMoney(value) {
    return money(-value.amount, value.currency);
}
export function absMoney(value) {
    return value.amount < 0 ? negateMoney(value) : value;
}
/**
 * An amount times a whole number: a unit price times a quantity. Exact — there is nothing to
 * round, and rounding here would be rounding an intermediate value (§1.4).
 */
export function multiplyMoney(value, factor) {
    assertInteger(factor, 'factor');
    const product = BigInt(value.amount) * BigInt(factor);
    return fromBigInt(product, value.currency);
}
/**
 * An amount times the ratio `numerator / denominator`, rounded half-up **once**, at the end.
 *
 * This is the primitive behind every proportional amount in the Platform — a pro-rata refund, a
 * share of a settlement, a charge for part of an hour. Callers pass the whole ratio rather than
 * multiplying and then dividing, so no intermediate value is ever rounded.
 */
export function scaleMoney(value, numerator, denominator) {
    assertInteger(numerator, 'numerator');
    assertInteger(denominator, 'denominator');
    const scaled = roundHalfUpBigInt(BigInt(value.amount) * BigInt(numerator), BigInt(denominator));
    return fromBigInt(scaled, value.currency);
}
/**
 * A percentage of an amount, in basis points, rounded half-up once: a discount, a commission, a
 * tax on an exclusive price. `applyBasisPoints({ amount: 50000, currency: 'INR' }, 250)` is ₹12.50.
 */
export function applyBasisPoints(value, bps) {
    assertInteger(bps, 'bps');
    return scaleMoney(value, bps, BASIS_POINTS_SCALE);
}
/**
 * The sum of a list. `currency` is required for an empty list — a sum of nothing still has to
 * carry a currency, and guessing one is how a mixed-currency bug hides.
 */
export function sumMoney(values, currency) {
    const first = values[0];
    if (first === undefined) {
        if (currency === undefined) {
            throw new DomainError('empty-allocation', 'Summing an empty list needs a currency to sum in.', {});
        }
        return zeroMoney(currency);
    }
    const currencyToUse = currency ?? first.currency;
    let total = 0n;
    for (const value of values) {
        assertSameCurrency({ amount: 0, currency: currencyToUse }, value);
        total += BigInt(value.amount);
    }
    return fromBigInt(total, currencyToUse);
}
/** −1, 0 or 1, for sorting and for comparisons that must not mix currencies by accident. */
export function compareMoney(a, b) {
    assertSameCurrency(a, b);
    if (a.amount < b.amount)
        return -1;
    if (a.amount > b.amount)
        return 1;
    return 0;
}
export function equalsMoney(a, b) {
    return a.currency === b.currency && a.amount === b.amount;
}
export function minMoney(a, b) {
    return compareMoney(a, b) <= 0 ? a : b;
}
export function maxMoney(a, b) {
    return compareMoney(a, b) >= 0 ? a : b;
}
/** Clamped to zero below: "a discount never takes a line below zero" (spec/06 §8.4 rule 2). */
export function clampMoneyToZero(value) {
    return value.amount < 0 ? zeroMoney(value.currency) : value;
}
/**
 * An amount split into halves, larger half first, losing and inventing nothing: the two parts
 * always sum back to the whole. This is what CGST + SGST is built on (spec/06 §10.1), and the odd
 * paisa goes to the first component — which is what the worked example in spec/06 §2.3 shows
 * (₹500 including 18% GST is CGST ₹38.14 + SGST ₹38.13).
 */
export function splitInHalf(value) {
    const first = scaleMoney(value, 1, 2);
    return [first, subtractMoney(value, first)];
}
/** Two amounts in one currency, or a `currency-mismatch`. */
export function assertSameCurrency(a, b) {
    if (a.currency !== b.currency) {
        throw new DomainError('currency-mismatch', `Cannot mix ${a.currency} and ${b.currency}: one order, one currency.`, {
            left: a.currency,
            right: b.currency,
        });
    }
}
/** Internal: back to a `Money`, with the API's range guarantee enforced (spec/06 §1.1). */
export function fromBigInt(amount, currency) {
    if (amount > BigInt(MONEY_MAX_MINOR_UNITS) || amount < BigInt(MONEY_MIN_MINOR_UNITS)) {
        throw new DomainError('money-out-of-range', `${amount} ${currency} is outside ±${MONEY_MAX_MINOR_UNITS} minor units.`, { amount: amount.toString(), currency });
    }
    return { amount: Number(amount), currency };
}
function assertAmountInRange(amount) {
    if (amount > MONEY_MAX_MINOR_UNITS || amount < MONEY_MIN_MINOR_UNITS) {
        throw new DomainError('money-out-of-range', `${amount} is outside ±${MONEY_MAX_MINOR_UNITS} minor units.`, {
            amount,
        });
    }
}
//# sourceMappingURL=arithmetic.js.map